import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import WeeklyCalendar from '../../components/WeeklyCalendar';
import TrendsBox from '../../components/TrendsBox';
import ScheduleCalendar from '../../components/ScheduleCalendar';
import { DailyMissions } from '../../components/DailyMissions';
import { TrendMediaModal, TrendMediaMatch } from '../../components/DailyMissions/TrendMediaModal';
import WeeklySummary from '../../components/WeeklySummary';
import SteamEventsBox from '../../components/SteamEventsBox';
import { brand } from '../../config/brand';
import { useProjects } from '../../contexts/ProjectContext';
import { usePostModal } from '../../contexts/PostModalContext';
import dailyTrendsData from '../../data/dailyTrends.json';
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import { Loader2, Plus, LayoutGrid, Trophy, Sparkles } from "lucide-react";
import { useGlobalState } from '../../contexts/GlobalStateContext';
import { getLevelTitle } from '../../utils/gamification';

import { API_BASE_URL } from '../../config';

interface Trend {
  id?: string;
  hashtag: string;
  name: string;
  description: string;
  template?: string; // Copyable format template (e.g. "[Your engine] // [Your game]")
  evidenceUrl?: string;
  evidenceText?: string;
  evidenceDate?: string;
  authorName?: string;
  authorHandle?: string;
  authorAvatar?: string;
  mediaUrl?: string;
  likeCount?: number;
  retweetCount?: number;
  lastSeen?: string;
  weight?: number;
  isCompleted?: boolean;
  isGeneric?: boolean;
}

export default function QuickSocialUploader(): JSX.Element {
  const { activeProject, projects, loading: projectsLoading } = useProjects();
  const { openPostModal, openPostById } = usePostModal();
  const { user } = useGlobalState();
  const token = localStorage.getItem('token');

  const [selectedDay, setSelectedDay] = useState<string>(''); // Day key (monday, etc.)
  const [selectedDate, setSelectedDate] = useState<string>(''); // Full date YYYY-MM-DD
  const [currentTrends, setCurrentTrends] = useState<Trend[]>([]);
  const [dynamicTrendDays, setDynamicTrendDays] = useState<string[]>([]);
  const [trendModalData, setTrendModalData] = useState<{ trendName: string; trend: Trend } | null>(null);
  const [completedTrendIds, setCompletedTrendIds] = useState<string[]>([]);
  const [dayPosts, setDayPosts] = useState<any[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(false);

  // Initialize selected day/date to today
  useEffect(() => {
    const today = new Date();
    const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const todayKey = dayKeys[today.getDay()];
    setSelectedDay(todayKey);
    setSelectedDate(today.toISOString().split('T')[0]);
  }, []);

  // Handle day selection from WeeklyCalendar
  const handleDaySelect = (dayKey: string) => {
    setSelectedDay(dayKey);

    // Calculate the date for the selected day in the 7-day sliding window
    const today = new Date();
    const todayDayIndex = today.getDay();
    const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const selectedDayIndex = dayKeys.indexOf(dayKey);

    // Note: This logic assumes the calendar window shows -3 to +3 from today
    // If we want to be exact, WeeklyCalendar should return the dateStr
    let diff = selectedDayIndex - todayDayIndex;
    // Handle wrap around if needed, but sliding window is simple:
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + diff);
    setSelectedDate(targetDate.toISOString().split('T')[0]);
  };

  // Fetch posts for the selected date
  useEffect(() => {
    if (!activeProject || !selectedDate) return;

    const fetchDayPosts = async () => {
      setLoadingPosts(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/posts/game/${activeProject.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        // Filter posts that have at least one schedule on the selected date
        const filtered = response.data.filter((post: any) => {
          return post.schedules.some((s: any) => {
            const sDate = new Date(s.scheduledAt || post.createdAt).toISOString().split('T')[0];
            return sDate === selectedDate;
          });
        });

        setDayPosts(filtered);
      } catch (err) {
        console.error('Error fetching posts for day:', err);
      } finally {
        setLoadingPosts(false);
      }
    };

    fetchDayPosts();
  }, [activeProject, selectedDate, token]);

  // Get trends logic
  useEffect(() => {
    if (!selectedDay) return;

    const fetchTrends = async () => {
      const allTrends: Trend[] = [];

      // 1. Load static trends (Daily & Monthly)
      const dailyTrends = (dailyTrendsData as any).dailyTrends as Record<string, any[]>;
      if (dailyTrends[selectedDay]) {
        allTrends.push(...dailyTrends[selectedDay].map(t => ({
          ...t,
          hashtag: t.hashtag || `#${t.name.replace(/\s+/g, '')}`,
          evidences: t.evidenceText ? [{ text: t.evidenceText, url: t.evidenceUrl }] : [],
          isGeneric: true // Static trends are generic
        })));
      }

      const today = new Date();
      const monthNames = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
      const monthlyTrends = (dailyTrendsData as any).monthlyTrends as Record<string, any[]>;
      if (monthlyTrends[monthNames[today.getMonth()]]) {
        allTrends.push(...monthlyTrends[monthNames[today.getMonth()]].map(t => ({
          ...t,
          hashtag: t.hashtag || `#${t.name.replace(/\s+/g, '')}`,
          evidences: t.evidenceText ? [{ text: t.evidenceText, url: t.evidenceUrl }] : [],
          isGeneric: true // Static trends are generic
        })));
      }

      // 2. Fetch dynamic trends from API
      try {
        const response = await axios.get(`${API_BASE_URL}/social/trends`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        const masterTrends = response.data.masterTrends || [];
        const completedIds = response.data.completedTrendIds || [];
        setCompletedTrendIds(completedIds);

        // Update days that have dynamic trends
        const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const detectedDays = new Set<string>();
        masterTrends.forEach((mt: any) => {
          if (mt.last_detected_date) {
            const date = new Date(mt.last_detected_date);
            detectedDays.add(dayKeys[date.getDay()]); // Use getDay() for local day
          }
        });
        setDynamicTrendDays(Array.from(detectedDays));

        // Calculate the date for the selected day in the current week
        const todayDayIndex = today.getDay();
        const selectedDayIndex = dayKeys.indexOf(selectedDay);

        const diff = selectedDayIndex - todayDayIndex;
        const targetDate = new Date(today);
        targetDate.setDate(today.getDate() + diff);
        const targetDateStr = targetDate.toISOString().split('T')[0];

        // Also get dates for today, yesterday, and day before yesterday
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().split('T')[0];

        const dayBefore = new Date(today);
        dayBefore.setDate(today.getDate() - 2);
        const dayBeforeStr = dayBefore.toISOString().split('T')[0];

        const todayStr = today.toISOString().split('T')[0];

        // Filter master trends: show if last_detected_date is targetDate OR if it's one of (today, yesterday, dayBefore)
        const filteredDynamicTrends = masterTrends.filter((mt: any) => {
          if (!mt.last_detected_date) return false;
          const lastDateStr = mt.last_detected_date;
          return lastDateStr === targetDateStr || lastDateStr === todayStr || lastDateStr === yesterdayStr || lastDateStr === dayBeforeStr;
        });

        // Map IMasterTrend to Trend interface
        const mappedDynamicTrends = filteredDynamicTrends.map((mt: any) => {
          const evidencePool = mt.evidences || [];

          const mappedEvidences = evidencePool.map((ev: any) => ({
            text: ev.text,
            url: ev.url,
            date: ev.publicationDate,
            authorName: ev.authorName,
            authorHandle: ev.authorHandle,
            authorAvatar: ev.authorAvatar,
            mediaUrl: ev.mediaUrl,
            likeCount: ev.likeCount,
            retweetCount: ev.retweetCount
          }));

          // Clean up name for hashtag: remove special characters like //, -, etc.
          const cleanHashtagName = mt.name.replace(/[^a-zA-Z0-9]/g, '');
          const trendHashtag = mt.name.startsWith('#') ? mt.name : `#${cleanHashtagName}`;

          return {
            id: mt.id,
            hashtag: trendHashtag,
            name: mt.name,
            template: mt.template || undefined,
            description: `Active for ${mt.active_days_count} days. Last seen: ${mt.last_detected_date}`,
            evidences: mappedEvidences,
            lastSeen: new Date(mt.last_detected_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
            weight: mt.total_occurrence_count,
            isCompleted: completedIds.includes(mt.id),
            isGeneric: false
          };
        });

        allTrends.push(...mappedDynamicTrends);
      } catch (err) {
        console.error('Error fetching dynamic trends:', err);
      }

      setCurrentTrends(allTrends);
    };

    fetchTrends();
  }, [selectedDay, token]);

  const handleTagClick = (hashtag: string) => {
    openPostModal({
      tags: [hashtag]
    });
  };

  const openPostForTrend = (trend: Trend, media?: any | any[]) => {
    let initialText = "";
    const trendName = trend.name;
    const hashtag = trend.hashtag;

    // Smart text pre-filling based on trend name
    if (trendName.includes("//")) {
      initialText = `${trendName} ${hashtag}\n\n`;
    } else if (trendName.toLowerCase().includes("wishlist")) {
      initialText = `It's ${trendName}! \n\nWishlist my game here: `;
    } else if (trendName.toLowerCase().includes("thursday") || trendName.toLowerCase().includes("monday") || trendName.toLowerCase().includes("friday")) {
      initialText = `Happy ${trendName}! ${hashtag}\n\n`;
    } else {
      initialText = hashtag.startsWith('#')
        ? `Working on my game! ${hashtag}`
        : `Working on my game! #${trendName.replace(/\s+/g, '')}`;
    }

    const mediaArray = Array.isArray(media) ? media : (media ? [media] : undefined);

    openPostModal({
      trendId: trend.id,
      tags: [hashtag],
      initialTextEn: initialText,
      media: mediaArray,
    }, { forceEdit: true });
  };

  const handleTrendClick = (trend: Trend) => {
    // Open AI media matching modal for all trends
    setTrendModalData({ trendName: trend.name, trend });
  };

  if (projectsLoading) {
    return (
      <div className="container mx-auto h-[80vh] flex flex-col items-center justify-center gap-4">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="text-muted-foreground font-medium">Loading your projects...</p>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="container mx-auto h-[80vh] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-card border-border text-center py-8">
          <CardHeader>
            <div className="mx-auto bg-primary/10 p-4 rounded-full w-fit mb-4">
              <Plus className="h-10 w-10 text-primary" />
            </div>
            <CardTitle className="text-3xl font-bold text-foreground">Welcome!</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-muted-foreground">You don't have any projects yet. Create one to start promoting your game!</p>
            <Button asChild className="bg-primary hover:bg-primary/90 text-white font-bold px-8 py-6 rounded-xl transition-all shadow-lg shadow-primary/20">
              <Link to="/projects">Create Your First Project</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!activeProject) {
    return (
      <div className="container mx-auto h-[80vh] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-card border-border text-center py-8">
          <CardHeader>
            <div className="mx-auto bg-primary/10 p-4 rounded-full w-fit mb-4">
              <LayoutGrid className="h-10 w-10 text-primary" />
            </div>
            <CardTitle className="text-3xl font-bold text-foreground">Select a Project</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-muted-foreground">Please select a project from the navbar or manage them below.</p>
            <Button asChild variant="outline" className="border-border text-foreground hover:bg-card px-8 py-6 rounded-xl">
              <Link to="/projects">Manage Projects</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl space-y-8">
      {/* Free Tier Banner */}
      {user?.tier === 'FREE' && (
        <div className="relative group overflow-hidden bg-gradient-to-r from-primary/10 via-card to-indigo-600/10 border border-primary/20 p-6 rounded-[32px] backdrop-blur-md shadow-2xl transition-all hover:scale-[1.005]">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 blur-[80px] rounded-full pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-indigo-600/5 blur-[60px] rounded-full pointer-events-none" />

          <div className="flex flex-col md:flex-row items-center justify-between gap-6 relative z-10">
            <div className="flex items-center gap-5">
              <div className="h-14 w-14 bg-primary/20 rounded-2xl flex items-center justify-center border border-primary/30 shadow-lg shrink-0">
                <Loader2 className="h-7 w-7 text-primary animate-spin-slow" />
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="bg-muted text-foreground text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border border-border">FREE TIER</span>
                  <h3 className="text-xl font-black text-foreground italic uppercase tracking-tighter">Your growth is on pause</h3>
                </div>
                <p className="text-muted-foreground text-sm font-medium leading-relaxed max-w-lg">
                  You are currently in <span className="text-primary font-bold">Safe Mode</span>. You can only <span className="text-foreground font-bold">view and delete</span> your content. Upgrade to start publishing your game content!
                </p>
              </div>
            </div>

            <Button asChild className="bg-primary hover:bg-primary/90 text-white font-black px-8 py-7 rounded-2xl transition-all shadow-xl shadow-primary/20 active:scale-95 group/btn">
              <Link to="/pricing" className="flex items-center gap-3">
                <Sparkles className="h-5 w-5 fill-white group-hover/btn:animate-pulse" />
                <span>SKYROCKET YOUR SUCCESS</span>
              </Link>
            </Button>
          </div>
        </div>
      )}

      {/* Header Stats Section */}
      <div className="flex flex-col md:flex-row gap-6 items-center justify-between bg-card/50 border border-border p-6 rounded-[32px] backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <div className="bg-primary p-3 rounded-2xl shadow-lg shadow-primary/20">
            <Trophy className="h-8 w-8 text-white" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-muted-foreground uppercase tracking-widest">Your Growth</h2>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-black text-foreground">Level {activeProject?.levelInfo?.level || 1}</span>
              <span className="text-primary font-bold">{getLevelTitle(activeProject?.levelInfo?.level || 1)}</span>
            </div>
          </div>
        </div>

        <div className="flex gap-8">
          <div className="text-center">
            <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-1">XP to Next Level</p>
            <div className="flex items-center gap-3">
              <div className="w-32 h-2 bg-card rounded-full overflow-hidden border border-border">
                <div
                  className="h-full bg-primary transition-all duration-1000"
                  style={{ width: `${activeProject?.levelInfo?.progressPercent || 0}%` }}
                />
              </div>
              <span className="text-xs font-bold text-foreground">{activeProject?.levelInfo?.currentXp || 0}/{activeProject?.levelInfo?.nextLevelXp || 100}</span>
            </div>
          </div>

          <div className="h-12 w-px bg-card" />

          <div className="text-center">
            <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-1">Missions Today</p>
            <div className="flex items-center justify-center gap-2">
              <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-2xl font-black text-foreground">
                {user?.totalMissionsCompletedToday || 0}
              </span>
              <span className="text-xs font-bold text-muted-foreground">Completed</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Daily Missions + Weekly Summary (50%) */}
        <div className="lg:col-span-6">
          {brand.features.showDailyMissions && activeProject && (
            <DailyMissions gameId={activeProject.id} />
          )}
          <WeeklySummary />
        </div>

        {/* Trends & Strategy (50%) */}
        <div className="lg:col-span-6">
          <Card className="bg-card border-border shadow-xl overflow-hidden h-fit">
            <CardHeader className="border-b border-border/50 bg-gradient-to-r from-primary/10 via-transparent to-transparent">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-black text-foreground uppercase tracking-[0.2em] flex items-center gap-2">
                  <div className="p-1.5 bg-primary/20 rounded-lg shadow-[0_0_15px_hsl(var(--primary)/0.2)]">
                    <LayoutGrid className="h-4 w-4 text-primary" />
                  </div>
                  Trends & Strategy
                </CardTitle>
                <span className="text-xs font-black text-muted-foreground uppercase tracking-widest">
                  {new Date().toLocaleString('en', { month: 'long' })}
                </span>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <WeeklyCalendar
                selectedDay={selectedDay}
                onDaySelect={handleDaySelect}
                daysWithTrends={Array.from(new Set([...Object.keys((dailyTrendsData as any).dailyTrends), ...dynamicTrendDays]))}
              />
              <div className="h-px bg-card mx-6" />
              <TrendsBox
                trends={currentTrends}
                dayPosts={dayPosts}
                onTagClick={handleTagClick}
                onTrendClick={handleTrendClick}
                onPostClick={(postId) => openPostById(postId)}
                title="X / Twitter Recent Trends"
              />
            </CardContent>
          </Card>

          {brand.features.showSteamEvents && <SteamEventsBox />}
        </div>
      </div>

      {/* Trend Media Selection Modal */}
      <TrendMediaModal
        isOpen={!!trendModalData}
        onClose={() => setTrendModalData(null)}
        trendName={trendModalData?.trendName || ''}
        onSelectMedia={(media: TrendMediaMatch | TrendMediaMatch[]) => {
          if (trendModalData) {
            openPostForTrend(trendModalData.trend, media);
          }
          setTrendModalData(null);
        }}
        onStartFromScratch={() => {
          if (trendModalData) {
            openPostForTrend(trendModalData.trend);
          }
          setTrendModalData(null);
        }}
      />
    </div>
  );
}
